import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import Clients from './pages/Clients';
import Attendance from './pages/Attendance';
import UserManagement from './pages/UserManagement';
import BulkImport from './pages/BulkImport';
import EmailTemplates from './pages/EmailTemplates';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import StaffList from './pages/HR/StaffList';
import StaffDocumentView from './pages/HR/StaffDocumentView';
import HRDashboard from './pages/HR/HRDashboard';
import RoleRoute from './components/RoleRoute';
import { useAuth } from './context/AuthContext';

// Layout wrapper for convenience
const AppLayout = ({ children }) => (
  <Layout>{children}</Layout>
);

/** HR uses one combined dashboard at /dashboard/staff/:id — never the legacy / home view */
function DashboardRoot() {
  const { user, loading } = useAuth();
  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }
  if (user && String(user.role || '').toUpperCase() === 'HR' && user.id != null) {
    return <Navigate to={`/dashboard/staff/${user.id}`} replace />;
  }
  return (
    <AppLayout>
      <Dashboard />
    </AppLayout>
  );
}

// Roles who can access the main dashboard and operational pages
const DASHBOARD_ROLES = ['ADMIN', 'SALES_TEAM_HEAD', 'SALES_TEAM', 'PROCESSING', 'STAFF', 'HR'];
// Roles who can access leads (includes HR since she gets assigned leads too)
const LEADS_ROLES = [...DASHBOARD_ROLES, 'HR'];
// Roles who can access HR section
const HR_ROLES = ['HR', 'ADMIN'];

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            path="/"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={DASHBOARD_ROLES}>
                  <DashboardRoot />
                </RoleRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/staff/:staffId"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={DASHBOARD_ROLES}>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/leads"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={LEADS_ROLES}>
                  <AppLayout>
                    <Leads />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/leads/:id"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={LEADS_ROLES}>
                  <AppLayout>
                    <LeadDetail />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/clients"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={DASHBOARD_ROLES}>
                  <AppLayout>
                    <Clients />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/clients/:id"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={DASHBOARD_ROLES}>
                  <AppLayout>
                    <Clients />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/attendance"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={[...DASHBOARD_ROLES, 'HR']}>
                  <AppLayout>
                    <Attendance />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/users"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={['ADMIN', 'HR']}>
                  <AppLayout>
                    <UserManagement />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/leads/import"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={DASHBOARD_ROLES}>
                  <AppLayout>
                    <BulkImport />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/email-templates"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={['ADMIN', 'HR']}>
                  <AppLayout>
                    <EmailTemplates />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />

          {/* HR Section - Protected from regular Staff */}
          <Route
            path="/hr"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={HR_ROLES}>
                  <AppLayout>
                    <HRDashboard />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/hr/staff"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={HR_ROLES}>
                  <AppLayout>
                    <StaffList />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/hr/staff/:id"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={HR_ROLES}>
                  <AppLayout>
                    <StaffDocumentView />
                  </AppLayout>
                </RoleRoute>
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
